(() => {
  'use strict';

  if (window.__neuChiefInteractionsLoaded) return;
  window.__neuChiefInteractionsLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const coarsePointer = window.matchMedia('(pointer: coarse)');
  const preparedCards = new WeakSet();
  const preparedTestimonials = new WeakSet();
  const preparedTimelines = new WeakSet();
  const preparedWindSurfaces = new WeakSet();
  const preparedOriginStories = new WeakSet();
  const timelines = [];
  const windSurfaces = [];
  const originStories = [];
  const knightFinds = new Set();
  let globalFrame = 0;
  let seamPrepared = false;
  let knightHuntPrepared = false;
  let orientationPrepared = false;

  const diagnostics = {
    cards: 0,
    inspectors: 0,
    testimonialDecks: 0,
    timelines: 0,
    cartAnimations: 0,
    originStories: 0,
    windSurfaces: 0,
    knightFinds: 0,
    filterRenders: 0,
    footerSeal: false,
  };

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
  const lerp = (start, end, amount) => start + (end - start) * amount;

  function isVisible(element) {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
  }

  function dispatchBambooEvent(type, element, extra = {}) {
    if (!(element instanceof Element)) return;
    const rect = element.getBoundingClientRect();
    document.dispatchEvent(
      new CustomEvent(type, {
        detail: {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          ...extra,
        },
      })
    );
  }

  function drawWeave(canvas) {
    const size = 132;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, size, size);
    context.lineCap = 'round';
    for (let coordinate = -size; coordinate < size * 2; coordinate += 7) {
      context.strokeStyle = coordinate % 14 === 0 ? 'rgba(43, 70, 44, 0.48)' : 'rgba(255, 248, 222, 0.42)';
      context.lineWidth = coordinate % 14 === 0 ? 1.25 : 0.8;
      context.beginPath();
      context.moveTo(coordinate, 0);
      context.lineTo(coordinate + size, size);
      context.stroke();
      context.beginPath();
      context.moveTo(coordinate + size, 0);
      context.lineTo(coordinate, size);
      context.stroke();
    }
  }

  function prepareProductCard(card) {
    if (!(card instanceof HTMLElement) || preparedCards.has(card)) return;
    preparedCards.add(card);
    card.classList.add('nc-interactive-card');
    diagnostics.cards += 1;

    const media = card.querySelector('card-media.card__media, .card--block-media card-media, .card--block-media');
    const viewport = media?.querySelector?.('.media--hover-effect') || media;
    const image = viewport?.querySelector?.('img:first-of-type');
    let lens = null;
    let longPress = null;
    let touchPress = null;
    let suppressClickUntil = 0;

    if (viewport && image) {
      lens = document.createElement('span');
      lens.className = 'nc-fabric-inspector';
      lens.setAttribute('aria-hidden', 'true');
      const weave = document.createElement('canvas');
      weave.setAttribute('aria-hidden', 'true');
      lens.append(weave);
      viewport.append(lens);
      drawWeave(weave);
      diagnostics.inspectors += 1;
    }

    const updateCardDepth = (event) => {
      if (coarsePointer.matches || reducedMotion.matches) return;
      const rect = card.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      card.style.setProperty('--nc-card-rotate-x', `${((0.5 - y) * 3.4).toFixed(2)}deg`);
      card.style.setProperty('--nc-card-rotate-y', `${((x - 0.5) * 4.4).toFixed(2)}deg`);
      card.style.setProperty('--nc-card-light-x', `${(x * 100).toFixed(1)}%`);
      card.style.setProperty('--nc-card-light-y', `${(y * 100).toFixed(1)}%`);
    };

    const resetCardDepth = () => {
      card.style.setProperty('--nc-card-rotate-x', '0deg');
      card.style.setProperty('--nc-card-rotate-y', '0deg');
      card.style.setProperty('--nc-card-light-x', '50%');
      card.style.setProperty('--nc-card-light-y', '35%');
    };

    const updateLens = (event) => {
      if (!lens || !viewport || !image) return;
      const rect = viewport.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = clamp(event.clientX - rect.left, 0, rect.width);
      const y = clamp(event.clientY - rect.top, 0, rect.height);
      const zoom = 3.1;
      const source = image.currentSrc || image.src;
      lens.style.left = `${x}px`;
      lens.style.top = `${y}px`;
      lens.style.backgroundImage = `url(${JSON.stringify(source)})`;
      lens.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
      lens.style.backgroundPosition = `${-(x * zoom - lens.offsetWidth / 2)}px ${-(y * zoom - lens.offsetHeight / 2)}px`;
    };

    card.addEventListener('pointermove', updateCardDepth, { passive: true });
    card.addEventListener('pointerleave', resetCardDepth, { passive: true });
    card.addEventListener('focusout', (event) => {
      if (!card.contains(event.relatedTarget)) resetCardDepth();
    });

    if (!viewport || !lens) return;
    const interactionSurface = media || viewport;

    interactionSurface.addEventListener('pointerenter', (event) => {
      if (coarsePointer.matches) return;
      card.classList.add('is-inspecting');
      updateLens(event);
    }, { passive: true });
    interactionSurface.addEventListener('pointermove', (event) => {
      if (!coarsePointer.matches) updateLens(event);
      if (!longPress || longPress.pointerId !== event.pointerId) return;
      const distance = Math.hypot(event.clientX - longPress.x, event.clientY - longPress.y);
      if (!longPress.active && distance > 9) {
        window.clearTimeout(longPress.timer);
        longPress = null;
        return;
      }
      if (longPress.active) {
        event.preventDefault();
        updateLens(event);
      }
    }, { passive: false });
    interactionSurface.addEventListener('pointerleave', () => {
      if (!coarsePointer.matches) card.classList.remove('is-inspecting');
    }, { passive: true });

    interactionSurface.addEventListener('pointerdown', (event) => {
      if (!coarsePointer.matches || event.isPrimary === false || event.pointerType === 'touch') return;
      const state = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        active: false,
        timer: 0,
      };
      state.timer = window.setTimeout(() => {
        if (longPress !== state) return;
        state.active = true;
        suppressClickUntil = performance.now() + 500;
        interactionSurface.setPointerCapture?.(event.pointerId);
        card.classList.add('is-inspecting');
        updateLens(event);
        dispatchBambooEvent('nc:bamboo-gather', interactionSurface, { duration: 900 });
      }, 280);
      longPress = state;
    }, { passive: true });

    const endLongPress = (event) => {
      if (!longPress || longPress.pointerId !== event.pointerId) return;
      const wasActive = longPress.active;
      window.clearTimeout(longPress.timer);
      longPress = null;
      if (wasActive) {
        event.preventDefault();
        card.classList.remove('is-inspecting');
        dispatchBambooEvent('nc:bamboo-scatter', interactionSurface, { count: 2 });
      }
    };
    interactionSurface.addEventListener('pointerup', endLongPress, { passive: false });
    interactionSurface.addEventListener('pointercancel', endLongPress, { passive: false });

    interactionSurface.addEventListener('touchstart', (event) => {
      if (!coarsePointer.matches || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const state = {
        identifier: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        active: false,
        timer: 0,
      };
      state.timer = window.setTimeout(() => {
        if (touchPress !== state) return;
        state.active = true;
        suppressClickUntil = performance.now() + 700;
        card.classList.add('is-inspecting');
        updateLens({ clientX: state.x, clientY: state.y });
        dispatchBambooEvent('nc:bamboo-gather', interactionSurface, { duration: 900 });
      }, 280);
      touchPress = state;
    }, { passive: true });

    interactionSurface.addEventListener('touchmove', (event) => {
      if (!touchPress) return;
      const touch = Array.from(event.touches).find((item) => item.identifier === touchPress.identifier);
      if (!touch) return;
      const distance = Math.hypot(touch.clientX - touchPress.x, touch.clientY - touchPress.y);
      if (!touchPress.active && distance > 9) {
        window.clearTimeout(touchPress.timer);
        touchPress = null;
        return;
      }
      if (!touchPress.active) return;
      event.preventDefault();
      card.classList.add('is-inspecting');
      updateLens(touch);
    }, { passive: false });

    const endTouchPress = (event) => {
      if (!touchPress) return;
      const wasActive = touchPress.active;
      window.clearTimeout(touchPress.timer);
      touchPress = null;
      if (!wasActive) return;
      event.preventDefault();
      suppressClickUntil = performance.now() + 500;
      card.classList.remove('is-inspecting');
      dispatchBambooEvent('nc:bamboo-scatter', interactionSurface, { count: 2 });
    };
    interactionSurface.addEventListener('touchend', endTouchPress, { passive: false });
    interactionSurface.addEventListener('touchcancel', endTouchPress, { passive: false });
    interactionSurface.addEventListener('contextmenu', (event) => {
      if (!touchPress?.active && !card.classList.contains('is-inspecting')) return;
      event.preventDefault();
    });

    card.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  }

  function prepareProductCards(root = document) {
    const cards = root.matches?.('.product-card-wrapper')
      ? [root]
      : Array.from(root.querySelectorAll?.('.product-card-wrapper') || []);
    cards.forEach(prepareProductCard);
  }

  function visibleCartControl() {
    return Array.from(document.querySelectorAll('#cart-icon-bubble, .cart-icon--drawer')).find(isVisible) || null;
  }

  function showCartConfirmation() {
    let status = document.querySelector('[data-nc-cart-confirmation]');
    if (!status) {
      status = document.createElement('div');
      status.className = 'nc-cart-confirmation';
      status.dataset.ncCartConfirmation = 'true';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      document.body.append(status);
    }
    status.textContent = 'Added to your bag';
    if (reducedMotion.matches || !status.animate) {
      status.style.opacity = '1';
      window.setTimeout(() => { status.style.opacity = '0'; }, 1400);
      return;
    }
    status.getAnimations().forEach((animation) => animation.cancel());
    status.animate(
      [
        { opacity: 0, transform: 'translateY(-10px)' },
        { opacity: 1, transform: 'translateY(0)', offset: 0.18 },
        { opacity: 1, transform: 'translateY(0)', offset: 0.78 },
        { opacity: 0, transform: 'translateY(-6px)' },
      ],
      { duration: 2200, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
  }

  function animateCartSuccess(event) {
    if (event?.source !== 'product-form-component') return;
    const origin = event.submitter instanceof Element ? event.submitter : null;
    const destination = visibleCartControl();
    showCartConfirmation();
    if (!destination) return;

    destination.classList.remove('nc-cart-icon-confirmed');
    void destination.offsetWidth;
    destination.classList.add('nc-cart-icon-confirmed');
    window.setTimeout(() => destination.classList.remove('nc-cart-icon-confirmed'), 680);

    if (reducedMotion.matches || !origin || !Element.prototype.animate) return;
    const productScope =
      origin.closest('quick-add-modal, product-info, .product-card-wrapper, .product-form') ||
      document.querySelector('product-info');
    const productImage = Array.from(
      productScope?.querySelectorAll?.('.product-media img, card-media img, .card__media img, img') || []
    ).find(isVisible);
    const from = (productImage || origin).getBoundingClientRect();
    const to = destination.getBoundingClientRect();
    if (!from.width || !to.width) return;
    const width = productImage ? clamp(from.width * 0.34, 74, 158) : 62;
    const height = productImage ? clamp(from.height * 0.34, 86, 188) : 22;
    const startX = from.left + from.width / 2 - width / 2;
    const startY = from.top + from.height / 2 - height / 2;
    const endX = to.left + to.width / 2 - width / 2;
    const endY = to.top + to.height / 2 - height / 2;
    const flight = document.createElement(productImage ? 'span' : 'span');
    flight.className = productImage ? 'nc-garment-fold-flight' : 'nc-fabric-flight';
    flight.setAttribute('aria-hidden', 'true');
    if (productImage) {
      const clone = productImage.cloneNode(false);
      clone.removeAttribute('id');
      clone.removeAttribute('loading');
      clone.alt = '';
      flight.style.width = `${width}px`;
      flight.style.height = `${height}px`;
      flight.append(clone);
    }
    document.body.append(flight);
    const middleX = startX + (endX - startX) * 0.54;
    const middleY = Math.min(startY, endY) - Math.min(130, window.innerHeight * 0.16);
    const keyframes = productImage
      ? [
          { transform: `translate3d(${startX}px, ${startY}px, 0) perspective(700px) rotateX(0) rotateY(0) scale(1)`, clipPath: 'inset(0 0 0 0 round 12px)', opacity: 1 },
          { transform: `translate3d(${startX}px, ${startY}px, 0) perspective(700px) rotateY(68deg) scaleX(0.72) scaleY(0.94)`, clipPath: 'inset(0 24% 0 24% round 12px)', offset: 0.2 },
          { transform: `translate3d(${startX + 18}px, ${startY - 12}px, 0) perspective(700px) rotateX(66deg) rotateY(0) scale(0.56)`, clipPath: 'inset(18% 24% 18% 24% round 12px)', offset: 0.4 },
          { transform: `translate3d(${middleX}px, ${middleY}px, 0) perspective(700px) rotateX(20deg) rotateY(-18deg) rotateZ(16deg) scale(0.38)`, clipPath: 'inset(18% 24% 18% 24% round 12px)', offset: 0.72 },
          { transform: `translate3d(${endX}px, ${endY}px, 0) perspective(700px) rotateZ(28deg) scale(0.06)`, clipPath: 'inset(22% 28% 22% 28% round 12px)', opacity: 0.18 },
        ]
      : [
          { transform: `translate3d(${startX}px, ${startY}px, 0) rotate(0) scale(1)` },
          { transform: `translate3d(${middleX}px, ${middleY}px, 0) rotate(132deg) scale(0.78)`, offset: 0.58 },
          { transform: `translate3d(${endX}px, ${endY}px, 0) rotate(330deg) scale(0.18)`, opacity: 0.45 },
        ];
    const animation = flight.animate(keyframes, {
      duration: productImage ? 980 : 720,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards',
    });
    animation.finished.catch(() => {}).finally(() => flight.remove());
    dispatchBambooEvent('nc:bamboo-scatter', destination, { count: 5, direction: -1 });
    diagnostics.cartAnimations += 1;
  }

  function prepareCartFeedback() {
    if (typeof window.subscribe !== 'function') return;
    const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
    window.subscribe(eventName, animateCartSuccess);
  }

  function prepareVariantFeedback() {
    document.addEventListener('change', (event) => {
      const input = event.target instanceof HTMLInputElement
        ? event.target.closest('variant-selects .variant-option--pill input[type="radio"]')
        : null;
      if (!input) return;
      const label = input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : input.nextElementSibling;
      if (!(label instanceof HTMLElement)) return;
      label.classList.remove('nc-size-stitch');
      void label.offsetWidth;
      label.classList.add('nc-size-stitch');
      window.setTimeout(() => label.classList.remove('nc-size-stitch'), 460);
      dispatchBambooEvent('nc:bamboo-scatter', label, { count: 2, direction: 1 });
    });
  }

  function drawBambooTimeline(timeline, progress) {
    const { canvas, host } = timeline;
    if (!host.isConnected) return;
    const rect = host.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, coarsePointer.matches ? 1 : 1.5);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    const width = rect.width;
    const height = rect.height;
    context.clearRect(0, 0, width, height);

    const path = new Path2D();
    path.moveTo(width * 0.54, height);
    path.bezierCurveTo(width * 0.38, height * 0.72, width * 0.7, height * 0.36, width * 0.46, 0);
    const pathLength = height * 1.12;
    context.lineCap = 'round';
    context.lineWidth = Math.max(3, width * 0.085);
    context.strokeStyle = 'rgba(245, 237, 221, 0.16)';
    context.stroke(path);
    context.setLineDash([pathLength, pathLength]);
    context.lineDashOffset = pathLength * (1 - progress);
    context.strokeStyle = '#b58e48';
    context.shadowColor = 'rgba(181, 142, 72, 0.38)';
    context.shadowBlur = 9;
    context.stroke(path);
    context.setLineDash([]);
    context.shadowBlur = 0;

    const milestones = timeline.milestones;
    for (let index = 0; index < milestones; index += 1) {
      const threshold = (index + 1) / (milestones + 1);
      const y = height * (1 - threshold);
      const x = width * (0.5 + Math.sin(index * 1.7) * 0.09);
      const active = progress >= threshold;
      context.strokeStyle = active ? '#d7bb73' : 'rgba(245, 237, 221, 0.16)';
      context.fillStyle = active ? 'rgba(119, 155, 115, 0.92)' : 'rgba(245, 237, 221, 0.12)';
      context.lineWidth = active ? 1.5 : 1;
      const direction = index % 2 === 0 ? -1 : 1;
      context.beginPath();
      context.moveTo(x, y);
      context.quadraticCurveTo(x + direction * width * 0.22, y - 8, x + direction * width * 0.36, y - 19);
      context.stroke();
      context.save();
      context.translate(x + direction * width * 0.39, y - 21);
      context.rotate(direction * -0.38);
      context.beginPath();
      context.ellipse(0, 0, Math.max(4, width * 0.12), Math.max(2, width * 0.045), 0, 0, Math.PI * 2);
      context.fill();
      context.restore();
    }
  }

  function prepareBambooTimeline(root = document) {
    const labs = root.matches?.('[data-nc-fabric-lab]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-nc-fabric-lab]') || []);
    labs.forEach((lab) => {
      if (preparedTimelines.has(lab)) return;
      const stage = lab.querySelector('.nc-fabric-lab__stage');
      if (!stage) return;
      preparedTimelines.add(lab);
      const host = document.createElement('span');
      host.className = 'nc-bamboo-timeline';
      host.setAttribute('aria-hidden', 'true');
      const canvas = document.createElement('canvas');
      host.append(canvas);
      stage.append(host);
      const timeline = {
        lab,
        host,
        canvas,
        progress: reducedMotion.matches ? 1 : 0,
        manualUntil: 0,
        milestones: Math.max(lab.querySelectorAll('[data-nc-fabric-point]').length, 4),
      };
      timelines.push(timeline);
      new ResizeObserver(() => drawBambooTimeline(timeline, timeline.progress)).observe(host);

      const range = lab.querySelector('[data-nc-fabric-range]');
      range?.addEventListener('input', () => {
        const minimum = Number(range.min || 0);
        const maximum = Number(range.max || 100);
        timeline.progress = clamp((Number(range.value) - minimum) / Math.max(maximum - minimum, 1), 0, 1);
        timeline.manualUntil = performance.now() + 1800;
        drawBambooTimeline(timeline, timeline.progress);
      });
      lab.querySelectorAll('[data-nc-fabric-point]').forEach((point, index, points) => {
        point.addEventListener('click', () => {
          timeline.progress = (index + 1) / points.length;
          timeline.manualUntil = performance.now() + 1800;
          drawBambooTimeline(timeline, timeline.progress);
          dispatchBambooEvent('nc:bamboo-scatter', point, { count: 2, direction: index % 2 ? -1 : 1 });
        });
      });
      drawBambooTimeline(timeline, timeline.progress);
      diagnostics.timelines += 1;
    });
  }

  function updateBambooTimelines() {
    const viewportHeight = Math.max(window.innerHeight, 1);
    for (let index = timelines.length - 1; index >= 0; index -= 1) {
      const timeline = timelines[index];
      if (!timeline.lab.isConnected) {
        timelines.splice(index, 1);
        continue;
      }
      if (reducedMotion.matches) {
        if (timeline.progress !== 1) {
          timeline.progress = 1;
          drawBambooTimeline(timeline, 1);
        }
        continue;
      }
      if (performance.now() < timeline.manualUntil) continue;
      const rect = timeline.lab.getBoundingClientRect();
      const nextProgress = clamp((viewportHeight - rect.top) / Math.max(rect.height + viewportHeight * 0.2, 1), 0, 1);
      if (Math.abs(nextProgress - timeline.progress) < 0.008) continue;
      timeline.progress = nextProgress;
      drawBambooTimeline(timeline, nextProgress);
    }
  }

  function prepareTestimonialDeck(deck) {
    if (!(deck instanceof HTMLElement) || preparedTestimonials.has(deck)) return;
    const track = deck.querySelector('.nc-testimonials__track');
    const cards = Array.from(deck.querySelectorAll('.nc-testimonials__card'));
    if (!track || cards.length < 2) return;
    preparedTestimonials.add(deck);
    deck.classList.add('nc-testimonials--stacked');
    track.tabIndex = 0;
    track.setAttribute('aria-roledescription', 'testimonial carousel');
    track.setAttribute('aria-label', 'Customer stories');

    const controls = document.createElement('div');
    controls.className = 'nc-testimonials__deck-controls';
    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'nc-testimonials__control nc-testimonials__control--previous';
    previous.setAttribute('aria-label', 'Previous testimonial');
    previous.innerHTML = '<span aria-hidden="true">\u2190</span>';
    const status = document.createElement('span');
    status.className = 'nc-testimonials__deck-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'nc-testimonials__control nc-testimonials__control--next';
    next.setAttribute('aria-label', 'Next testimonial');
    next.innerHTML = '<span aria-hidden="true">\u2192</span>';
    controls.append(previous, status, next);
    const mobileHint = deck.querySelector('.nc-testimonials__mobile-hint');
    if (mobileHint) mobileHint.before(controls);
    else deck.append(controls);

    let activeIndex = 0;
    let drag = null;

    const measure = () => {
      const height = Math.max(...cards.map((card) => card.scrollHeight), 280) + 20;
      deck.style.setProperty('--nc-testimonial-height', `${height}px`);
    };

    const show = (nextIndex, announce = true) => {
      activeIndex = (nextIndex + cards.length) % cards.length;
      cards.forEach((card, index) => {
        let offset = index - activeIndex;
        if (offset > cards.length / 2) offset -= cards.length;
        if (offset < -cards.length / 2) offset += cards.length;
        card.dataset.ncOffset = Math.abs(offset) > 1 ? 'far' : String(offset);
        card.toggleAttribute('aria-current', index === activeIndex);
      });
      track.style.setProperty('--nc-testimonial-drag', '0px');
      status.textContent = `Story ${activeIndex + 1} of ${cards.length}`;
      if (announce) dispatchBambooEvent('nc:bamboo-scatter', track, { count: 2, direction: 1 });
      measure();
    };

    previous.addEventListener('click', () => show(activeIndex - 1));
    next.addEventListener('click', () => show(activeIndex + 1));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(activeIndex + 1);
      }
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target.closest('a, button, input, select, textarea')) return;
      drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), moved: false };
      track.setPointerCapture?.(event.pointerId);
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - drag.x;
      const deltaY = event.clientY - drag.y;
      if (!drag.moved && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
        drag = null;
        return;
      }
      if (Math.abs(deltaX) > 6) drag.moved = true;
      if (!drag.moved) return;
      event.preventDefault();
      track.style.setProperty('--nc-testimonial-drag', `${clamp(deltaX, -140, 140)}px`);
    }, { passive: false });

    const endDrag = (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - drag.x;
      const velocity = deltaX / Math.max(performance.now() - drag.time, 1);
      const shouldMove = Math.abs(deltaX) > 46 || Math.abs(velocity) > 0.36;
      const direction = deltaX < 0 ? 1 : -1;
      drag = null;
      if (shouldMove) show(activeIndex + direction);
      else track.style.setProperty('--nc-testimonial-drag', '0px');
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', () => {
      drag = null;
      track.style.setProperty('--nc-testimonial-drag', '0px');
    });

    const resizeObserver = new ResizeObserver(measure);
    cards.forEach((card) => resizeObserver.observe(card));
    deck.addEventListener('nc:testimonial-select', (event) => show(Number(event.detail?.index) || 0));
    show(0, false);
    diagnostics.testimonialDecks += 1;
  }

  function prepareTestimonials(root = document) {
    const decks = root.matches?.('[data-nc-testimonials]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-nc-testimonials]') || []);
    decks.forEach(prepareTestimonialDeck);
  }

  function prepareFooterSeal() {
    const footer = document.querySelector('.section-footer');
    if (footer && footer.dataset.ncFooterEnhanced !== 'true') {
      footer.dataset.ncFooterEnhanced = 'true';
      footer.classList.add('nc-footer-enhanced');

      const columns = Array.from(
        footer.querySelectorAll(
          '.section-content-wrapper > .group-block > .group-block-content > .group-block'
        )
      );
      columns.forEach((column, index) => {
        column.classList.add('nc-footer-item');
        column.style.setProperty('--nc-footer-index', String(index));
      });

      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        footer.classList.add('nc-footer-visible');
      } else {
        const footerObserver = new IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            footer.classList.add('nc-footer-visible');
            observer.disconnect();
          },
          { threshold: 0.08, rootMargin: '0px 0px -3% 0px' }
        );
        footerObserver.observe(footer);
      }
    }

    const emailInput = Array.from(
      document.querySelectorAll('.section-footer input[type="email"], footer input[type="email"]')
    ).find(isVisible);
    if (!emailInput) return;
    const newsletter = emailInput.closest('email-signup, .email-signup, form') || emailInput.parentElement;
    if (!newsletter || newsletter.querySelector('[data-nc-footer-seal]')) return;
    newsletter.classList.add('nc-footer-newsletter');
    if (!newsletter.id) newsletter.id = 'NeuChiefFooterNewsletter';

    const wrapper = document.createElement('div');
    wrapper.className = 'nc-footer-seal-wrap';
    const seal = document.createElement('button');
    seal.type = 'button';
    seal.className = 'nc-footer-seal';
    seal.dataset.ncFooterSeal = 'true';
    seal.setAttribute('aria-controls', newsletter.id);
    seal.setAttribute('aria-expanded', window.Shopify?.designMode ? 'true' : 'false');
    seal.setAttribute('aria-label', 'Open the Neuchief newsletter signup');
    // Use the site's chess-piece logo (read from an existing logo <img> so no URL is hardcoded)
    const sealLogo = document.querySelector('.header-logo__image, .logo-block__image, [data-main-logo] img');
    const sealLogoSrc = sealLogo ? (sealLogo.currentSrc || sealLogo.getAttribute('src')) : '';
    if (sealLogoSrc) {
      seal.classList.add('nc-footer-seal--logo');
      seal.innerHTML = '<img class="nc-footer-seal__logo" src="' + sealLogoSrc + '" alt="" aria-hidden="true">';
    } else {
      seal.innerHTML = '<span>Neuchief<br>Join</span>';
    }
    wrapper.append(seal);
    newsletter.append(wrapper);

    seal.addEventListener('pointermove', (event) => {
      const rect = seal.getBoundingClientRect();
      seal.style.setProperty('--nc-seal-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
      seal.style.setProperty('--nc-seal-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    }, { passive: true });
    seal.addEventListener('pointerleave', () => {
      seal.style.removeProperty('--nc-seal-x');
      seal.style.removeProperty('--nc-seal-y');
    }, { passive: true });
    seal.addEventListener('click', () => {
      newsletter.classList.add('nc-footer-newsletter--open');
      seal.setAttribute('aria-expanded', 'true');
      emailInput.focus({ preventScroll: true });
      dispatchBambooEvent('nc:bamboo-scatter', seal, { count: 6, direction: -1 });
      window.setTimeout(() => newsletter.classList.remove('nc-footer-newsletter--open'), 2400);
    });
    diagnostics.footerSeal = true;
  }

  function setOriginStoryStage(entry, active, keepStepVisible = true) {
    const safeActive = clamp(active, 0, entry.stages.length - 1);
    const previousActive = entry.active;
    const direction = previousActive < 0 || safeActive >= previousActive ? 'forward' : 'reverse';
    entry.active = safeActive;
    entry.story.classList.toggle('is-forward', direction === 'forward');
    entry.story.classList.toggle('is-reverse', direction === 'reverse');
    entry.stages.forEach((stage, index) => stage.classList.toggle('is-active', index === safeActive));
    entry.copies.forEach((copy, index) => {
      const selected = index === safeActive;
      copy.classList.toggle('is-active', selected);
      if (selected) copy.setAttribute('aria-current', 'step');
      else copy.removeAttribute('aria-current');
    });
    entry.markers.forEach((marker, index) => marker.classList.toggle('is-complete', index <= safeActive));
    const heading = entry.copies[safeActive]?.querySelector('strong')?.textContent?.trim();
    const action = entry.stageWords[safeActive] || entry.copies[safeActive]?.querySelector('.nc-origin-story__step-action')?.textContent?.trim();
    if (entry.label && heading) entry.label.textContent = heading;
    if (entry.word && action && (previousActive !== safeActive || !entry.word.textContent)) {
      entry.word.textContent = action.toUpperCase();
      entry.word.classList.remove('is-changing');
      window.requestAnimationFrame(() => entry.word?.classList.add('is-changing'));
    }
    if (entry.counter) entry.counter.textContent = `Stage ${safeActive + 1} of ${entry.stages.length}`;
    if (entry.progressMeter) {
      entry.progressMeter.setAttribute('aria-valuenow', String(safeActive + 1));
      entry.progressMeter.setAttribute('aria-valuetext', heading ? `Stage ${safeActive + 1}: ${heading}` : `Stage ${safeActive + 1}`);
    }
    entry.hotspots.forEach((hotspot) => {
      const visible = Number(hotspot.dataset.originStage) === safeActive;
      hotspot.classList.toggle('is-visible', visible);
      hotspot.setAttribute('aria-hidden', String(!visible));
      hotspot.tabIndex = visible ? 0 : -1;
      if (!visible) {
        hotspot.classList.remove('is-open');
        hotspot.setAttribute('aria-expanded', 'false');
      }
    });
    if (keepStepVisible && window.innerWidth < 750) {
      const activeCopy = entry.copies[safeActive];
      const steps = activeCopy?.parentElement;
      if (activeCopy && steps?.scrollTo) {
        steps.scrollTo({
          left: activeCopy.offsetLeft - (steps.clientWidth - activeCopy.offsetWidth) / 2,
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });
      }
    }
  }

  function navigateOriginStory(entry, index, behavior = 'smooth') {
    const safeIndex = clamp(index, 0, entry.stages.length - 1);
    entry.story.classList.add('has-interacted');
    if (safeIndex < entry.stages.length - 1) entry.story.classList.remove('is-complete');
    setOriginStoryStage(entry, safeIndex);
    if (reducedMotion.matches) return;
    const rect = entry.story.getBoundingClientRect();
    const storyTop = window.scrollY + rect.top;
    const scrollDistance = Math.max(entry.story.offsetHeight - window.innerHeight, 1);
    window.scrollTo({
      top: storyTop + (safeIndex / (entry.stages.length - 1)) * scrollDistance,
      behavior,
    });
  }

  function prepareOriginStory(story) {
    if (!(story instanceof HTMLElement) || preparedOriginStories.has(story)) return;
    const visual = story.querySelector('[data-nc-origin-visual]');
    const stages = Array.from(story.querySelectorAll('[data-nc-origin-stage]'));
    const copies = Array.from(story.querySelectorAll('[data-nc-origin-copy]'));
    const label = story.querySelector('[data-nc-origin-label]');
    const counter = story.querySelector('[data-nc-origin-counter]');
    const progressMeter = story.querySelector('[data-nc-origin-progress]');
    const markers = Array.from(story.querySelectorAll('.nc-origin-story__progress-track i'));
    const triggers = copies.map((copy) => copy.querySelector('[data-nc-origin-trigger]'));
    const word = story.querySelector('[data-nc-origin-word]');
    const hotspots = Array.from(story.querySelectorAll('[data-nc-origin-hotspot]'));
    const replay = story.querySelector('[data-nc-origin-replay]');
    if (!visual || stages.length < 2 || copies.length !== stages.length) return;
    preparedOriginStories.add(story);
    const entry = {
      story,
      visual,
      stages,
      copies,
      label,
      counter,
      progressMeter,
      markers,
      word,
      hotspots,
      replay,
      stageWords: ['GROW', 'REFINE', 'WEAVE', 'TAILOR'],
      active: -1,
      lastProgress: null,
    };
    originStories.push(entry);
    triggers.forEach((trigger, index) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      trigger.addEventListener('click', () => {
        navigateOriginStory(entry, index);
      });
      trigger.addEventListener('keydown', (event) => {
        const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
            ? -1
            : 0;
        if (!direction) return;
        event.preventDefault();
        const nextIndex = clamp(index + direction, 0, triggers.length - 1);
        triggers[nextIndex]?.focus();
        triggers[nextIndex]?.click();
      });
    });
    visual.addEventListener('pointermove', (event) => {
      if (coarsePointer.matches || reducedMotion.matches) return;
      const rect = visual.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5, -0.5, 0.5);
      const y = clamp((event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5, -0.5, 0.5);
      story.style.setProperty('--nc-origin-pointer-x', `${(x * -14).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-pointer-y', `${(y * -10).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-svg-x', `${(x * 7).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-svg-y', `${(y * 5).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-leaf-tilt', `${(x * 10).toFixed(2)}deg`);
      story.style.setProperty('--nc-origin-leaf-shift', `${(x * 8).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-fibre-pointer-rotate', `${(x * 5).toFixed(2)}deg`);
      story.style.setProperty('--nc-origin-sheen-x', `${(x * 44).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-sheen-y', `${(y * 30).toFixed(2)}px`);
      story.style.setProperty('--nc-origin-sheen-opacity', (0.28 + Math.abs(x) * 0.48).toFixed(3));
    }, { passive: true });
    visual.addEventListener('pointerleave', () => {
      story.style.removeProperty('--nc-origin-pointer-x');
      story.style.removeProperty('--nc-origin-pointer-y');
      story.style.removeProperty('--nc-origin-svg-x');
      story.style.removeProperty('--nc-origin-svg-y');
      story.style.removeProperty('--nc-origin-leaf-tilt');
      story.style.removeProperty('--nc-origin-leaf-shift');
      story.style.removeProperty('--nc-origin-fibre-pointer-rotate');
      story.style.removeProperty('--nc-origin-sheen-x');
      story.style.removeProperty('--nc-origin-sheen-y');
      story.style.removeProperty('--nc-origin-sheen-opacity');
    }, { passive: true });
    hotspots.forEach((hotspot) => {
      hotspot.addEventListener('click', () => {
        const willOpen = !hotspot.classList.contains('is-open');
        hotspots.forEach((item) => {
          item.classList.remove('is-open');
          item.setAttribute('aria-expanded', 'false');
        });
        hotspot.classList.toggle('is-open', willOpen);
        hotspot.setAttribute('aria-expanded', String(willOpen));
        story.classList.add('has-interacted');
      });
    });
    replay?.addEventListener('click', () => navigateOriginStory(entry, 0));

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartedOnControl = false;
    visual.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartedOnControl = event.target instanceof Element && Boolean(event.target.closest('a, button'));
    }, { passive: true });
    visual.addEventListener('touchend', (event) => {
      if (touchStartedOnControl) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      navigateOriginStory(entry, entry.active + (deltaX < 0 ? 1 : -1));
    }, { passive: true });
    diagnostics.originStories += 1;
  }

  function prepareOriginStories(root = document) {
    const stories = root.matches?.('[data-nc-origin-story]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-nc-origin-story]') || []);
    stories.forEach(prepareOriginStory);
  }

  function updateOriginStories() {
    originStories.forEach((entry) => {
      if (!entry.story.isConnected) return;
      const rect = entry.story.getBoundingClientRect();
      const scrollDistance = Math.max(rect.height - window.innerHeight, 1);
      const progress = reducedMotion.matches ? 1 : clamp(-rect.top / scrollDistance, 0, 1);
      const position = progress * (entry.stages.length - 1);
      const active = Math.round(position);
      if (entry.lastProgress !== null && Math.abs(progress - entry.lastProgress) > 0.0005) {
        const forward = progress > entry.lastProgress;
        entry.story.classList.toggle('is-forward', forward);
        entry.story.classList.toggle('is-reverse', !forward);
      }
      entry.lastProgress = progress;
      if (progress > 0.025) entry.story.classList.add('has-interacted');
      entry.story.classList.toggle('is-complete', active === entry.stages.length - 1 && position >= entry.stages.length - 1.18);
      entry.story.style.setProperty('--nc-origin-progress', progress.toFixed(4));
      entry.story.style.setProperty('--nc-origin-halo-scale', (0.92 + progress * 0.1).toFixed(4));
      entry.story.style.setProperty('--nc-origin-bamboo-y', `${(progress * -18).toFixed(2)}px`);
      entry.story.style.setProperty('--nc-origin-bamboo-scale', (1 - progress * 0.04).toFixed(4));
      entry.story.style.setProperty('--nc-origin-fibre-rotate', `${((progress - 0.33) * 5).toFixed(2)}deg`);
      entry.story.style.setProperty('--nc-origin-fibre-scale', (0.94 + progress * 0.05).toFixed(4));
      entry.story.style.setProperty('--nc-origin-fibre-offset', `${(680 * (1 - clamp(position, 0, 1))).toFixed(2)}px`);
      entry.story.style.setProperty('--nc-origin-weave-rotate', `${((progress - 0.66) * -3).toFixed(2)}deg`);
      entry.story.style.setProperty('--nc-origin-weave-scale', (0.9 + progress * 0.08).toFixed(4));
      entry.story.style.setProperty('--nc-origin-weave-inset', `${(48 * (1 - clamp(position - 1, 0, 1))).toFixed(2)}%`);
      entry.story.style.setProperty('--nc-origin-polo-y', `${((1 - progress) * 22).toFixed(2)}px`);
      entry.story.style.setProperty('--nc-origin-polo-scale', (0.9 + progress * 0.1).toFixed(4));
      entry.story.style.setProperty('--nc-origin-thread-offset', `${(260 - progress * 260).toFixed(2)}px`);
      entry.story.style.setProperty('--nc-origin-thread-opacity', (0.25 + progress * 0.75).toFixed(3));
      const cameraScales = [0.94, 1.12, 1.2, 1];
      const cameraX = [0, -18, 12, 0];
      const cameraY = [8, 0, -8, 0];
      const cameraLow = Math.floor(position);
      const cameraHigh = Math.min(cameraLow + 1, cameraScales.length - 1);
      const cameraMix = position - cameraLow;
      entry.story.style.setProperty('--nc-origin-camera-scale', lerp(cameraScales[cameraLow], cameraScales[cameraHigh], cameraMix).toFixed(4));
      entry.story.style.setProperty('--nc-origin-camera-x', `${lerp(cameraX[cameraLow], cameraX[cameraHigh], cameraMix).toFixed(2)}px`);
      entry.story.style.setProperty('--nc-origin-camera-y', `${lerp(cameraY[cameraLow], cameraY[cameraHigh], cameraMix).toFixed(2)}px`);
      entry.stages.forEach((stage, index) => {
        const opacity = clamp(1 - Math.abs(position - index), 0, 1);
        stage.style.setProperty('--nc-origin-opacity', opacity.toFixed(3));
      });
      if (active === entry.active) return;
      setOriginStoryStage(entry, active);
    });
  }

  function setWind(surface, x, y, active = true) {
    const safeX = clamp(x, -1, 1);
    const safeY = clamp(y, -1, 1);
    surface.style.setProperty('--nc-wind-x', `${(safeX * 11).toFixed(2)}px`);
    surface.style.setProperty('--nc-wind-y', `${(safeY * 7).toFixed(2)}px`);
    surface.style.setProperty('--nc-wind-shear', `${(safeX * 1.6).toFixed(2)}deg`);
    surface.style.setProperty('--nc-wind-image-x', `${(safeX * 2.42).toFixed(2)}px`);
    surface.style.setProperty('--nc-wind-image-y', `${(safeY * 1.26).toFixed(2)}px`);
    surface.style.setProperty('--nc-wind-image-shear', `${(safeX * 0.26).toFixed(2)}deg`);
    surface.style.setProperty('--nc-wind-light-x', `${(50 + safeX * 26).toFixed(1)}%`);
    surface.classList.toggle('nc-wind-active', active && !reducedMotion.matches);
  }

  function resetWind(surface) {
    setWind(surface, 0, 0, false);
  }

  function prepareWindSurface(surface) {
    if (!(surface instanceof HTMLElement) || preparedWindSurfaces.has(surface)) return;
    if (!surface.querySelector('img')) return;
    preparedWindSurfaces.add(surface);
    windSurfaces.push(surface);
    surface.classList.add('nc-wind-surface');
    diagnostics.windSurfaces += 1;

    surface.addEventListener('pointermove', (event) => {
      if (coarsePointer.matches || reducedMotion.matches) return;
      const rect = surface.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      setWind(
        surface,
        ((event.clientX - rect.left) / rect.width - 0.5) * 2,
        ((event.clientY - rect.top) / rect.height - 0.5) * 2
      );
    }, { passive: true });
    surface.addEventListener('pointerleave', () => resetWind(surface), { passive: true });
    surface.addEventListener('touchmove', (event) => {
      if (reducedMotion.matches || event.touches.length !== 1) return;
      const rect = surface.getBoundingClientRect();
      const touch = event.touches[0];
      setWind(
        surface,
        ((touch.clientX - rect.left) / Math.max(rect.width, 1) - 0.5) * 1.2,
        ((touch.clientY - rect.top) / Math.max(rect.height, 1) - 0.5) * 1.2
      );
    }, { passive: true });
    surface.addEventListener('touchend', () => resetWind(surface), { passive: true });
  }

  function prepareFabricWind(root = document) {
    const selector = '.slide__image-container, .product-card-wrapper card-media > .media, .product-media-container .product-media';
    const surfaces = root.matches?.(selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(selector) || []);
    surfaces.forEach(prepareWindSurface);
    if (orientationPrepared || !('DeviceOrientationEvent' in window)) return;
    orientationPrepared = true;
    window.addEventListener('deviceorientation', (event) => {
      if (reducedMotion.matches || event.gamma == null || event.beta == null) return;
      const x = clamp(event.gamma / 24, -1, 1);
      const y = clamp((event.beta - 45) / 38, -1, 1);
      windSurfaces.forEach((surface) => {
        if (!surface.isConnected || !isVisible(surface)) return;
        const rect = surface.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        setWind(surface, x, y);
      });
    }, { passive: true });
  }

  function prepareGoldenSeam() {
    if (seamPrepared) return;
    seamPrepared = true;
    let overlay = document.querySelector('[data-nc-seam-transition]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'nc-seam-transition';
      overlay.dataset.ncSeamTransition = 'true';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML = '<span class="nc-seam-transition__thread"></span>';
      document.body.append(overlay);
    }

    try {
      const hasIncomingSeam = window.sessionStorage.getItem('nc-seam-open-v1') === 'true';
      const seamStartedAt = Number(window.sessionStorage.getItem('nc-seam-start-v1'));
      if (hasIncomingSeam) {
        window.sessionStorage.removeItem('nc-seam-open-v1');
        window.sessionStorage.removeItem('nc-seam-start-v1');
      }
      if (hasIncomingSeam && !reducedMotion.matches) {
        if (Number.isFinite(seamStartedAt) && seamStartedAt > 0) {
          const seamElapsed = Math.max(Date.now() - seamStartedAt, 0) % 3600;
          document.documentElement.style.setProperty('--nc-seam-motion-delay', `-${seamElapsed}ms`);
        }
        const preloadScreen = document.querySelector('.preload-screen');
        if (preloadScreen) {
          preloadScreen.dataset.ncSeamSuppressed = 'true';
          preloadScreen.classList.add('off', 'loaded');
        }
        document.documentElement.classList.add('nc-seam-is-entering');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => document.documentElement.classList.add('nc-seam-is-opening'));
        });
        window.setTimeout(() => {
          document.documentElement.classList.remove('nc-seam-is-entering', 'nc-seam-is-opening');
          document.documentElement.style.removeProperty('--nc-seam-motion-delay');
        }, 860);
      } else {
        document.documentElement.classList.remove('nc-seam-is-entering');
        document.documentElement.style.removeProperty('--nc-seam-motion-delay');
      }
    } catch (_error) {
      // Session storage is optional; the outgoing seam still works.
      document.documentElement.classList.remove('nc-seam-is-entering');
    }

    document.addEventListener('click', (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        reducedMotion.matches ||
        window.Shopify?.designMode
      ) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!anchor || anchor.hasAttribute('download') || anchor.dataset.noTransition != null) return;
      if (anchor.target && anchor.target.toLowerCase() !== '_self') return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
      let destination;
      try {
        destination = new URL(anchor.href, window.location.href);
      } catch (_error) {
        return;
      }
      if (!/^https?:$/.test(destination.protocol) || destination.origin !== window.location.origin) return;
      if (
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search &&
        destination.hash
      ) return;
      event.preventDefault();
      try {
        window.sessionStorage.setItem('nc-seam-open-v1', 'true');
        window.sessionStorage.setItem('nc-seam-start-v1', String(Date.now()));
      } catch (_error) {
        // Ignore storage restrictions.
      }
      document.documentElement.classList.add('nc-seam-is-leaving');
      window.setTimeout(() => window.location.assign(destination.href), 620);
    }, true);

    window.addEventListener('pageshow', () => {
      document.documentElement.classList.remove('nc-seam-is-leaving');
      if (!document.documentElement.classList.contains('nc-seam-is-entering')) {
        document.documentElement.style.removeProperty('--nc-seam-motion-delay');
      }
    });
  }

  function loadKnightFinds() {
    try {
      const stored = JSON.parse(window.localStorage.getItem('nc-knight-finds-v1') || '[]');
      if (Array.isArray(stored)) stored.forEach((index) => knightFinds.add(Number(index)));
    } catch (_error) {
      // Storage is optional; the hunt still works for the current page view.
    }
  }

  function saveKnightFinds() {
    try {
      window.localStorage.setItem('nc-knight-finds-v1', JSON.stringify(Array.from(knightFinds)));
    } catch (_error) {
      // Ignore storage restrictions.
    }
  }

  function ensureKnightProgress() {
    let progress = document.querySelector('[data-nc-knight-progress]');
    if (progress) return progress;
    progress = document.createElement('div');
    progress.className = 'nc-knight-progress';
    progress.dataset.ncKnightProgress = 'true';
    progress.setAttribute('role', 'status');
    progress.setAttribute('aria-live', 'polite');
    document.body.append(progress);
    return progress;
  }

  function showKnightProgress() {
    const progress = ensureKnightProgress();
    progress.textContent = `${knightFinds.size} of 3 hidden knights found`;
    if (!progress.animate) {
      progress.style.opacity = '1';
      window.setTimeout(() => { progress.style.opacity = '0'; }, 1800);
      return;
    }
    progress.getAnimations().forEach((animation) => animation.cancel());
    progress.animate(
      [
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)', offset: 0.15 },
        { opacity: 1, transform: 'translateY(0)', offset: 0.78 },
        { opacity: 0, transform: 'translateY(8px)' },
      ],
      { duration: 2300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
  }

  function showKnightStory() {
    let dialog = document.querySelector('[data-nc-knight-story]');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'nc-knight-story';
      dialog.dataset.ncKnightStory = 'true';
      dialog.setAttribute('aria-labelledby', 'NcKnightStoryTitle');
      dialog.innerHTML = `
        <div class="nc-knight-story__inner">
          <span class="nc-knight-story__piece" aria-hidden="true">&#9822;</span>
          <h2 id="NcKnightStoryTitle">The Three Knights</h2>
          <p>You found Neuchief's quiet guardians: nature, craft, and confidence. Together they turn bamboo-rich comfort into clothing made for every move of the day.</p>
          <button type="button" data-nc-knight-close>Return to the collection</button>
        </div>`;
      document.body.append(dialog);
      dialog.querySelector('[data-nc-knight-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    }
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function prepareKnightHunt() {
    if (!document.body.classList.contains('template-index')) return;
    if (!knightHuntPrepared) {
      knightHuntPrepared = true;
      loadKnightFinds();
    }
    const selectors = ['[data-nc-origin-story]', '[data-nc-fabric-lab]', '[data-nc-testimonials]'];
    const knightSvg = '<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M9 26h17v3H6v-3h3Zm2-2c-1-7 1-12 6-15l-3-5c7 1 12 5 13 12-4-2-7-1-9 2h7l-2 6H11Z"/></svg>';
    selectors.forEach((selector, index) => {
      const host = document.querySelector(selector);
      if (!host || host.querySelector(`[data-nc-knight-index="${index}"]`)) return;
      host.classList.add('nc-knight-host');
      const knight = document.createElement('button');
      knight.type = 'button';
      knight.className = 'nc-knight-find';
      knight.dataset.ncKnightIndex = String(index);
      knight.innerHTML = knightSvg;
      const found = knightFinds.has(index);
      knight.classList.toggle('is-found', found);
      knight.setAttribute('aria-label', found ? `Hidden knight ${index + 1} found` : `Find hidden knight ${index + 1} of 3`);
      knight.addEventListener('click', () => {
        if (knightFinds.has(index)) {
          showKnightProgress();
          return;
        }
        knightFinds.add(index);
        diagnostics.knightFinds = knightFinds.size;
        saveKnightFinds();
        knight.classList.add('is-found');
        knight.setAttribute('aria-label', `Hidden knight ${index + 1} found`);
        showKnightProgress();
        dispatchBambooEvent('nc:bamboo-scatter', knight, { count: 7, direction: index % 2 ? -1 : 1 });
        if (knightFinds.size === selectors.length) window.setTimeout(showKnightStory, 720);
      });
      host.append(knight);
    });
    diagnostics.knightFinds = knightFinds.size;
  }

  function preparePageTransitions() {
    document.documentElement.classList.remove('nc-view-transitions');
    prepareGoldenSeam();
  }

  function updateGlobalFrame() {
    globalFrame = 0;
    updateBambooTimelines();
    updateOriginStories();
  }

  function scheduleGlobalFrame() {
    if (globalFrame) return;
    globalFrame = window.requestAnimationFrame(updateGlobalFrame);
  }

  function refresh(root = document) {
    prepareProductCards(root);
    prepareBambooTimeline(root);
    prepareTestimonials(root);
    prepareOriginStories(root);
    prepareFabricWind(root);
    prepareKnightHunt();
    prepareFooterSeal();
    scheduleGlobalFrame();
  }

  function initialize() {
    document.documentElement.classList.add('nc-interactions-ready');
    preparePageTransitions();
    prepareCartFeedback();
    prepareVariantFeedback();
    refresh(document);

    window.addEventListener('scroll', scheduleGlobalFrame, { passive: true });
    window.addEventListener('resize', scheduleGlobalFrame, { passive: true });
    document.addEventListener('product-grid:rendered', (event) => {
      diagnostics.filterRenders += 1;
      refresh(event.detail?.container || document);
    });
    document.addEventListener('shopify:section:load', (event) => refresh(event.target));
    document.addEventListener('shopify:section:reorder', () => refresh(document));
    document.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest?.('.nc-testimonials__card');
      const deck = card?.closest?.('[data-nc-testimonials]');
      if (!card || !deck) return;
      const cards = Array.from(deck.querySelectorAll('.nc-testimonials__card'));
      deck.dispatchEvent(new CustomEvent('nc:testimonial-select', { detail: { index: cards.indexOf(card) } }));
    });
    window.addEventListener('pageshow', () => {
      preparePageTransitions();
      refresh(document);
    });
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', () => {
        preparePageTransitions();
        scheduleGlobalFrame();
      });
    }
  }

  window.NeuChiefInteractions = Object.freeze({
    refresh,
    getDiagnostics: () => ({
      ...diagnostics,
      reducedMotion: reducedMotion.matches,
      coarsePointer: coarsePointer.matches,
      connectedTimelines: timelines.filter((timeline) => timeline.lab.isConnected).length,
    }),
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
